import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

const Header = () => {
  const { t, i18n } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'zh-CN' ? 'en' : 'zh-CN';
    i18n.changeLanguage(newLang);
    localStorage.setItem('coolpay-lang', newLang);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { key: 'home', id: 'hero' },
    { key: 'features', id: 'features' },
    { key: 'scenarios', id: 'scenarios' },
    { key: 'integration', id: 'integration' },
    { key: 'faq', id: 'faq' },
    { key: 'contact', id: 'contact' }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-slate-900/95 backdrop-blur-md border-b border-slate-800' 
          : 'bg-transparent'
      }`}>
        <nav className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => scrollToSection('hero')}>
              <img 
                src="/images/coolpay-logo.png" 
                alt="CoolPay" 
                className="w-8 h-8"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                CoolPay
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.id)}
                  className="text-gray-300 hover:text-cyan-400 transition-colors duration-300 font-medium relative group"
                >
                  {t(`nav.${item.key}`)}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </div>

            {/* Language Switcher & Mobile Menu */}
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleLanguage}
                className="hidden md:flex items-center px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 hover:border-cyan-400 transition-colors duration-300"
              >
                <span className="text-sm font-medium text-gray-300">
                  {i18n.language === 'zh-CN' ? '中文' : 'EN'}
                </span>
                <i className="ri-global-line ml-1 text-cyan-400"></i>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-cyan-400 transition-colors"
              >
                <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/95 backdrop-blur-md md:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-8">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.id)}
                className="text-2xl font-medium text-gray-300 hover:text-cyan-400 transition-colors"
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
            <button
              onClick={toggleLanguage}
              className="flex items-center px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-gray-300"
            >
              {i18n.language === 'zh-CN' ? '中文' : 'EN'}
              <i className="ri-global-line ml-2 text-cyan-400"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;